import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, ArrowRight, ArrowLeft, Sparkles, ShieldCheck, ChevronDown, Check, StickyNote, AlertTriangle } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useProperty } from "../lib/PropertyContext";
import PropertySwitcher from "../components/customer/PropertySwitcher";
import NoteTemplatesRow from "../components/NoteTemplatesRow";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";
import { absenceCoveredByChain, coverageWarningMessage, chainStart, recommendedPackageFromStart } from "../lib/servicePackageLimits";
import ServicePackageBooker from "../components/customer/ServicePackageBooker";

const STD_SUBCAT_LABEL_DE = {
  interior:   "Innenbereich",
  outdoor:    "Außenbereich",
  management: "Verwaltung",
};
const STD_SUBCAT_LABEL_EN = {
  interior:   "Interior",
  outdoor:    "Outdoor",
  management: "Management",
};
const STD_SUBCAT_ORDER = ["interior", "outdoor", "management"];

/**
 * "Was steht an?" — Kunde plant seine Abwesenheit
 * 1) Abreise-Datum (Kunde verlässt das Haus)
 * 2) Anreise-Datum (Kunde kommt zurück)
 * 3) Zusatz-Services für diesen Zeitraum
 * 4) Bestätigen → an Team + Kalender
 *
 * Die persönliche Abreise-/Anreise-Checkliste ("Fenster · Alarm · Müll …")
 * wird nach Erstellen der Buchung direkt in ihrem Detail-Fenster auf dem
 * Home-Screen abgehakt. So kann der Kunde bis kurz vor Abreise nachtragen.
 */
export default function CustomerArrival() {
  const nav = useNavigate();
  const { t, lang } = useI18n();
  const de = lang === "de";
  const STD_SUBCAT_LABEL = de ? STD_SUBCAT_LABEL_DE : STD_SUBCAT_LABEL_EN;
  // Feb 2026 · 8 Sep 2026 · Objekt-Umschalter global: properties + selected
  // aus dem Context — Form-Feld `property_id` bindet an `selected.id`.
  const { properties, selected } = useProperty();
  const [extras, setExtras] = useState([]);
  const [standardServices, setStandardServices] = useState([]);
  const [lastExtraIds, setLastExtraIds] = useState([]); // extras from most recent booking
  const [existingBookings, setExistingBookings] = useState([]); // for overlap detection
  const [bookedPackage, setPkgBookings] = useState([]); // service package bookings (per property)
  const [form, setForm] = useState({
    property_id: "",
    from_date: "",
    to_date: "",
    services: [],
    disabled_standard_services: [],
    service_notes: {},
    notes: "",
  });
  const [openNoteKeys, setOpenNoteKeys] = useState(new Set());
  const [inclusiveOpen, setInclusiveOpen] = useState(false); // COLLAPSED default
  const [extrasOpen, setExtrasOpen] = useState(false); // COLLAPSED default
  const [submitting, setSubmitting] = useState(false);

  // Geführte Abfolge: 0 Zeitraum · 1 Inklusivleistungen · 2 Zusatzleistungen · 3 Besondere Wünsche · 4 Bestätigen
  const [step, setStep] = useState(0);
  // Beim Betreten eines Listen-Schritts die Liste aufklappen; bei JEDEM
  // Schrittwechsel an den Seitenanfang scrollen (v. a. mobil sichtbar starten).
  useEffect(() => {
    if (step === 1) setInclusiveOpen(true);
    if (step === 2) setExtrasOpen(true);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  useEffect(() => {
    (async () => {
      const [e, s, b] = await Promise.all([
        api.get("/extras"),
        api.get("/standard-services"),
        api.get("/bookings"),
      ]);
      setExtras(e.data);
      setStandardServices(s.data || []);
      setExistingBookings(b.data || []);
      // Determine extras from most recent booking (by created_at desc)
      const sorted = [...b.data].sort((a, x) => (x.created_at || "").localeCompare(a.created_at || ""));
      const mostRecentWithExtras = sorted.find((bk) => (bk.services || []).length > 0);
      if (mostRecentWithExtras) setLastExtraIds(mostRecentWithExtras.services);
    })();
  }, []);
  // Feb 2026 · 8 Sep 2026 · property_id im Form immer auf das global aktive
  // Objekt binden — Wechsel im Dashboard reflektiert hier sofort.
  useEffect(() => {
    if (selected?.id) setForm((f) => ({ ...f, property_id: selected.id }));
  }, [selected?.id]);

  // Aktuell gebuchtes Service-Paket laden — für die Deckungsprüfung und die
  // Inline-Buchung. Fehler ignorieren, Warnung ist optional.
  const loadPackages = async () => {
    try {
      const { data } = await api.get("/service-packages/mine");
      setPkgBookings(data || []);
    } catch (e) { /* Warnung ist optional — Flow nie blockieren */ }
  };
  useEffect(() => { loadPackages(); }, []);

  const extrasByCategory = useMemo(() => {
    return extras.reduce((acc, ex) => {
      (acc[ex.category] = acc[ex.category] || []).push(ex);
      return acc;
    }, {});
  }, [extras]);

  const stdBySubcategory = useMemo(() => {
    const buckets = { interior: [], outdoor: [], management: [] };
    for (const s of standardServices || []) {
      const sub = (s.subcategory || "management").toLowerCase();
      (buckets[sub] || buckets.management).push(s);
    }
    return buckets;
  }, [standardServices]);

  const disabledSet = useMemo(() => new Set(form.disabled_standard_services || []), [form.disabled_standard_services]);
  const stdTotal = (standardServices || []).length;
  const stdEnabledCount = stdTotal - disabledSet.size;
  const inclusiveNotesCount = useMemo(
    () => (standardServices || []).filter((s) => (form.service_notes?.[s.key] || "").trim()).length,
    [standardServices, form.service_notes]
  );

  // Client-side overlap detection: warn the user before they even try to
  // submit. Any non-cancelled booking whose date range touches the selected
  // range triggers the "Sie haben für diesen Zeitraum bereits eine
  // Abwesenheit geplant" hint. Server enforces the same rule (HTTP 409).
  const [serverError, setServerError] = useState(null);
  const overlap = useMemo(() => {
    if (!form.from_date || !form.to_date) return null;
    if (form.from_date > form.to_date) return null;
    return (existingBookings || []).find((b) => {
      // STRIKT pro Objekt: Abwesenheiten anderer Objekte dürfen niemals als
      // Überschneidung zählen (existingBookings enthält alle Objekte des Kunden).
      if (b.property_id !== form.property_id) return false;
      if (b.status === "cancelled") return false;
      if (!b.from_date || !b.to_date) return false;
      // [a1,a2] ∩ [b1,b2] ≠ ∅  iff  a1 ≤ b2 AND b1 ≤ a2
      return b.from_date <= form.to_date && b.to_date >= form.from_date;
    }) || null;
  }, [existingBookings, form.property_id, form.from_date, form.to_date]);

  // Reset any server error banner as soon as the user picks new dates.
  useEffect(() => { setServerError(null); }, [form.from_date, form.to_date]);

  const toggleStdEnabled = (key) => setForm((f) => {
    const cur = new Set(f.disabled_standard_services || []);
    if (cur.has(key)) cur.delete(key);
    else cur.add(key);
    return { ...f, disabled_standard_services: Array.from(cur) };
  });

  const setNoteFor = (k, v) => setForm((f) => ({
    ...f,
    service_notes: { ...(f.service_notes || {}), [k]: v },
  }));

  const toggleNoteEditor = (k) => setOpenNoteKeys((cur) => {
    const next = new Set(cur);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    return next;
  });

  const nights = useMemo(() => {
    if (!form.from_date || !form.to_date) return 0;
    const a = new Date(form.from_date);
    const b = new Date(form.to_date);
    return Math.max(0, Math.round((b - a) / 86400000));
  }, [form.from_date, form.to_date]);

  // Modell A · Service-Paket-Deckung: Die Abwesenheit muss VOLLSTÄNDIG in die
  // Paket-Kette des Objekts fallen (Union der lückenlosen Kette). Sonst neutraler
  // Hinweis (gold) + Buchungsweg. Gleiche Logik wie Backend — kein Bypass.
  const packageWarning = useMemo(() => {
    if (!form.property_id || !form.from_date || !form.to_date) return null;
    if (form.from_date > form.to_date) return null;
    if (absenceCoveredByChain(bookedPackage, form.property_id, form.from_date, form.to_date)) return null;
    return coverageWarningMessage(form.from_date, form.to_date, de);
  }, [bookedPackage, form.property_id, form.from_date, form.to_date, de]);

  // Empfohlenes Paket für den (noch) nicht gedeckten Zeitraum: kürzestes
  // ausreichendes — ab dem nahtlosen Anschluss (Folgepaket) bzw. ab Abreise
  // (erstes Paket).
  const recommendedPkg = useMemo(() => {
    if (!packageWarning) return null;
    const ci = chainStart(bookedPackage, form.property_id, new Date().toISOString().slice(0, 10), form.from_date);
    if (ci.mode === "open") return null;
    const start = ci.mode === "follow" ? ci.start : form.from_date;
    return recommendedPackageFromStart(start, form.to_date);
  }, [packageWarning, bookedPackage, form.property_id, form.from_date, form.to_date]);

  const canSubmit =
    form.property_id &&
    form.from_date &&
    form.to_date &&
    form.from_date <= form.to_date &&
    !overlap &&
    !packageWarning &&
    !submitting;

  // Schritt 1 (Zeitraum) ist gültig, wenn Datum/Objekt passen und weder eine
  // Überschneidung noch eine Paket-Deckungslücke vorliegt.
  const step0Valid =
    form.property_id &&
    form.from_date &&
    form.to_date &&
    form.from_date <= form.to_date &&
    !overlap &&
    !packageWarning;
  const goNext = () => setStep((s) => Math.min(4, s + 1));
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const STEP_LABELS = de
    ? ["Zeitraum", "Inklusivleistungen", "Zusatzleistungen", "Besondere Wünsche", "Bestätigen"]
    : ["Period", "Included services", "Add-on services", "Special wishes", "Confirm"];

  const toggleExtra = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id) ? f.services.filter((x) => x !== id) : [...f.services, id],
    }));
  };

  const submit = async (e, opts = {}) => {
    e?.preventDefault();
    // Absenden ausschließlich im letzten Schritt (verhindert Enter-Submit früher).
    if (step !== 4) return;
    if (!canSubmit) return;
    setSubmitting(true);
    setServerError(null);
    try {
      // "Überspringen" → ohne besondere Wünsche bestätigen.
      const payload = opts.skipWishes ? { ...form, notes: "" } : { ...form };
      // Backend seeds the personal Abreise-/Anreise-Checkliste automatically.
      // Der Kunde hakt sie später im Detail-Fenster jeder Abwesenheit ab.
      await api.post("/bookings", payload);
      toast.success(de ? "Bestätigt · Ihr Weissblom-Team ist informiert." : "Confirmed · your Weissblom team has been notified.");
      nav("/app");
    } catch (err) {
      // 409: overlap with an existing absence — show inline banner so the
      // user can adjust the dates without hunting through a toast.
      if (err.response?.status === 409) {
        setServerError(formatErr(err.response?.data?.detail));
      } else {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const categoryLabels = t.catalogue.categories;

  const extraById = useMemo(() => Object.fromEntries(extras.map((ex) => [ex.id, ex])), [extras]);
  const lastExtras = useMemo(
    () => lastExtraIds.map((id) => extraById[id]).filter(Boolean),
    [lastExtraIds, extraById],
  );
  const allLastApplied = lastExtras.length > 0 && lastExtras.every((ex) => form.services.includes(ex.id));

  const applyLastExtras = () => {
    if (lastExtras.length === 0) return;
    setForm((f) => {
      const merged = Array.from(new Set([...f.services, ...lastExtras.map((ex) => ex.id)]));
      return { ...f, services: merged };
    });
    toast.success(de
      ? `${lastExtras.length} Zusatzleistung${lastExtras.length === 1 ? "" : "en"} von letzter Buchung übernommen.`
      : `${lastExtras.length} extra${lastExtras.length === 1 ? "" : "s"} added from your last booking.`);
  };

  return (
    <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-10 md:py-16" data-testid="customer-arrival">
      <div className="mb-10">
        <div className="label-caps mb-3">Weissblom Concierge</div>
        <h1 className="font-display text-4xl md:text-5xl text-navy">
          {de ? "Planen Sie Ihre Abwesenheit" : "Plan your absence"}
        </h1>
        <p className="text-[15px] text-navy-dark/70 mt-3 max-w-2xl">
          {de
            ? "Wählen Sie Zeitraum und Leistungen aus. Wir kümmern uns in dieser Zeit zuverlässig um Ihr Zuhause."
            : "Choose your period and services. We reliably take care of your home during this time."}
        </p>
      </div>

      {/* Feb 2026 · 8 Sep 2026 · Globaler Objekt-Umschalter — Formular-Feld
          `property_id` bindet automatisch an den aktiven Objekt. */}
      {step === 0 && <PropertySwitcher testidPrefix="arrival-property-switcher" />}

      {/* --- Fortschritts-Stepper (1–4) --- */}
      <ol className="flex items-center gap-2 md:gap-3 mb-6 flex-wrap" data-testid="arrival-stepper">
        {STEP_LABELS.map((label, i) => {
          const done = i < step;
          const active = i === step;
          return (
            <li key={i} className="flex items-center gap-2" data-testid={`arrival-step-indicator-${i}`}>
              <span
                className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 border"
                style={
                  active
                    ? { background: BRAND_COLORS.gold, borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.navy }
                    : done
                    ? { background: withAlpha(BRAND_COLORS.gold, 0.18), borderColor: BRAND_COLORS.gold, color: BRAND_COLORS.gold }
                    : { background: "#fff", borderColor: withAlpha(BRAND_COLORS.navy, 0.25), color: withAlpha(BRAND_COLORS.navy, 0.55) }
                }
                data-active={active ? "true" : "false"}
              >
                {done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : i + 1}
              </span>
              <span className={`text-[11px] tracking-[0.14em] uppercase ${active ? "text-navy" : "text-navy-dark/45"}`}>
                {label}
              </span>
              {i < STEP_LABELS.length - 1 && (
                <span className="hidden md:inline-block w-6 border-t" style={{ borderColor: withAlpha(BRAND_COLORS.navy, 0.15) }} />
              )}
            </li>
          );
        })}
      </ol>

      <form onSubmit={submit} className="bg-white border border-navy/10 p-8 md:p-10 space-y-10" data-testid="arrival-form">

        {/* ===== SCHRITT 1: ZEITRAUM ===== */}
        {step === 0 && (
        <div className="space-y-10" data-testid="arrival-step-dates">

        {/* --- Prominente Datums-Auswahl --- */}
        <div>
          <div className="label-caps mb-6" style={{ color: BRAND_COLORS.gold }}>
            {de ? "Ihr Zeitraum" : "Your period"}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 md:gap-4 items-end">
            <div className="border-l-4 border-gold pl-5 py-2">
              <label htmlFor="abreise-date" className="label-caps mb-2 block text-navy">
                {de ? "Abreise" : "Departure"}
              </label>
              <div className="text-[12px] text-navy-dark/60 italic mb-3">
                {de ? "Datum, an dem Sie das Haus verlassen" : "Date you leave the house"}
              </div>
              <input
                id="abreise-date"
                data-testid="abreise-date-input"
                type="date"
                required
                value={form.from_date}
                onChange={(e) => setForm({ ...form, from_date: e.target.value, to_date: form.to_date && form.to_date >= e.target.value ? form.to_date : e.target.value })}
                className="w-full bg-ivory/50 border border-navy/20 focus:border-gold focus:outline-none px-4 py-4 text-navy-dark font-display text-xl"
              />
            </div>

            <div className="hidden md:flex items-center justify-center pb-6">
              <ArrowRight className="w-6 h-6" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
            </div>

            <div className="border-l-4 border-gold pl-5 py-2">
              <label htmlFor="anreise-date" className="label-caps mb-2 block text-navy">
                {de ? "Anreise" : "Return"}
              </label>
              <div className="text-[12px] text-navy-dark/60 italic mb-3">
                {de ? "Datum, an dem Sie zurückkommen" : "Date you return"}
              </div>
              <input
                id="anreise-date"
                data-testid="anreise-date-input"
                type="date"
                required
                value={form.to_date}
                min={form.from_date || undefined}
                onChange={(e) => setForm({ ...form, to_date: e.target.value })}
                className="w-full bg-ivory/50 border border-navy/20 focus:border-gold focus:outline-none px-4 py-4 text-navy-dark font-display text-xl"
              />
            </div>
          </div>
          {nights > 0 && (
            <div className="mt-4 text-[13px] text-navy-dark/60" data-testid="arrival-nights">
              {de ? "Abwesenheit" : "Absence"}: <span className="text-navy font-medium">{nights} {de ? (nights === 1 ? "Nacht" : "Nächte") : (nights === 1 ? "night" : "nights")}</span>
            </div>
          )}

          {/* Modell A · Deckungslücke: Die Abwesenheit fällt (noch) nicht in die
              Paket-Kette. Statt wegzunavigieren kann der Kunde das passende Paket
              DIREKT hier buchen — nach der Buchung verschwindet die Warnung und
              „Bestätigen" wird frei. */}
          {packageWarning && (
            <div className="mt-4 space-y-4" data-testid="arrival-package-coverage-block">
              <div
                className="flex items-start gap-3 border p-4 rounded"
                style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), borderColor: BRAND_COLORS.gold }}
                data-testid="arrival-package-duration-warning"
                role="alert"
              >
                <AlertTriangle
                  className="w-5 h-5 mt-0.5 shrink-0"
                  strokeWidth={1.8}
                  style={{ color: BRAND_COLORS.gold }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium leading-snug text-navy">
                    {packageWarning}
                  </div>
                  <div className="mt-1 text-[12px] text-navy-dark/70 leading-snug">
                    {de
                      ? "Buchen Sie das passende Service-Paket direkt hier — danach können Sie Ihre Abwesenheit bestätigen."
                      : "Book the matching service package right here — then you can confirm your absence."}
                  </div>
                </div>
              </div>

              <ServicePackageBooker
                bookings={bookedPackage}
                absences={existingBookings}
                propertyId={form.property_id}
                propertyName={selected?.name || ""}
                multi={properties.length > 1}
                de={de}
                presetPackageId={recommendedPkg}
                defaultStart={form.from_date}
                requiredCoverage={{ from: form.from_date, to: form.to_date }}
                onBooked={loadPackages}
                testidPrefix="arrival-service-packages"
              />
            </div>
          )}

          {/* Overlap warning — client-side detection + 409 fallback from server.
              Both surface the same friendly message so the customer knows to
              change the existing booking instead of creating a duplicate. */}
          {(overlap || serverError) && (
            <div
              className="mt-4 flex items-start gap-3 border p-4 rounded"
              style={{ background: withAlpha(STATUS_COLORS.red, 0.06), borderColor: STATUS_COLORS.red }}
              data-testid="arrival-overlap-warning"
              role="alert"
            >
              <AlertTriangle
                className="w-5 h-5 mt-0.5 shrink-0"
                strokeWidth={1.8}
                style={{ color: STATUS_COLORS.red }}
              />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium leading-snug" style={{ color: STATUS_COLORS.red }}>
                  {de
                    ? "Sie haben für diesen Zeitraum bereits eine Abwesenheit geplant. Bitte ändern Sie den bestehenden Termin."
                    : "You already have an absence planned for this period. Please adjust the existing entry."}
                </div>
                {overlap && (
                  <div
                    className="mt-1 text-[12px] leading-snug"
                    style={{ color: withAlpha(STATUS_COLORS.red, 0.85) }}
                    data-testid="arrival-overlap-details"
                  >
                    {de ? "Bestehender Termin:" : "Existing entry:"}{" "}
                    <span className="font-medium">
                      {deDateMedium(overlap.from_date)}
                      {" → "}
                      {deDateMedium(overlap.to_date)}
                    </span>
                    .
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => nav("/app/requests")}
                  data-testid="arrival-overlap-open-existing"
                  className="mt-2 text-[11px] tracking-[0.14em] uppercase underline"
                  style={{ color: STATUS_COLORS.red }}
                >
                  {de ? "Bestehenden Termin öffnen →" : "Open existing entry →"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* --- Objekt (nur wenn mehrere) --- */}
        {properties.length > 1 && (
          <div>
            <label htmlFor="arrival-property" className="label-caps block mb-2">
              {de ? "Objekt" : "Property"}
            </label>
            <select
              id="arrival-property"
              data-testid="arrival-property-select"
              value={form.property_id}
              onChange={(e) => setForm({ ...form, property_id: e.target.value })}
              className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark"
            >
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        {properties.length === 1 && (
          <div className="text-[13px] text-navy-dark/60">
            <span className="label-caps mr-2">{de ? "Objekt:" : "Property:"}</span>
            <span className="text-navy">{properties[0].name}</span>
          </div>
        )}

        {/* Weiter → geführte Leistungsauswahl */}
        <div className="flex justify-end pt-4 border-t border-navy/10">
          <button
            type="button"
            data-testid="arrival-step-dates-next"
            disabled={!step0Valid}
            onClick={goNext}
            className="inline-flex items-center gap-3 text-ivory px-10 py-4 text-[13px] tracking-[0.2em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40"
            style={{ background: BRAND_COLORS.navy }}
          >
            {de ? "Weiter zur Leistungsauswahl" : "Continue to services"}
            <ArrowRight className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
          </button>
        </div>
        </div>
        )}

        {/* ===== SCHRITT 2: INKLUSIVLEISTUNGEN ===== */}
        {step === 1 && (
        <div className="space-y-8" data-testid="arrival-step-inclusive">
          {/* Gold-Hinweis-Banner (exakter Text) */}
          <div
            className="flex items-start gap-3 border p-5 rounded"
            style={{ background: withAlpha(BRAND_COLORS.gold, 0.10), borderColor: BRAND_COLORS.gold }}
            data-testid="arrival-services-hint"
            role="note"
          >
            <Sparkles className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.6} style={{ color: BRAND_COLORS.gold }} />
            <div className="text-[13.5px] leading-relaxed text-navy">
              {de
                ? "Alles wie gewünscht? Prüfen Sie vor dem Speichern, ob Sie alle Inklusivleistungen wünschen – und welche Zusatzleistungen Sie maßgeschneidert ergänzen möchten."
                : "All as you wish? Before saving, please check whether you want all included services – and which add-on services you'd like to add, tailored to you."}
            </div>
          </div>

        {/* --- INKLUSIV-LEISTUNGEN --- */}
        {stdTotal > 0 && (
          <div
            className="border border-navy/10 bg-white/60"
            data-testid="arrival-inclusive-section"
          >
            <button
              type="button"
              onClick={() => setInclusiveOpen((v) => !v)}
              data-testid="arrival-inclusive-toggle"
              className="w-full flex items-center justify-between text-left px-4 py-3.5"
              aria-expanded={inclusiveOpen}
            >
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" style={{ color: STATUS_COLORS.green }} strokeWidth={1.6} />
                <span className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/85">
                  {de ? "Inklusiv-Leistungen" : "Included services"}
                </span>
                <span
                  className="text-[10.5px] tracking-widest uppercase text-navy-dark/50"
                  data-testid="arrival-inclusive-count"
                >
                  ({stdEnabledCount} {de ? "von" : "of"} {stdTotal} {de ? "aktiv" : "active"}{inclusiveNotesCount > 0 ? ` · ${inclusiveNotesCount} ${de ? "mit Notiz" : "with note"}` : ""})
                </span>
              </span>
              <ChevronDown
                className={`w-4 h-4 text-navy-dark/50 transition-transform ${inclusiveOpen ? "rotate-180" : ""}`}
                strokeWidth={1.6}
              />
            </button>
            {!inclusiveOpen && (
              <div className="px-4 pb-3 -mt-1 text-[11.5px] text-navy-dark/55 italic leading-relaxed">
                {de
                  ? "Diese Leistungen sind Teil Ihrer Mitgliedschaft. Haken entfernen, wenn eine Leistung bei diesem Termin nicht ausgeführt werden soll. Notizfeld für individuelle Wünsche."
                  : "These services are part of your membership. Untick when a service should not be performed this time. Note field for individual wishes."}
              </div>
            )}
            {inclusiveOpen && (
              <div className="px-4 pb-4 pt-1 space-y-4">
                {STD_SUBCAT_ORDER.map((sub) => {
                  const list = stdBySubcategory[sub];
                  if (!list || list.length === 0) return null;
                  const enabled = list.filter((s) => !disabledSet.has(s.key)).length;
                  return (
                    <div key={sub} data-testid={`arrival-inclusive-group-${sub}`}>
                      <div className="flex items-baseline justify-between mb-1.5">
                        <div className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/70">
                          {STD_SUBCAT_LABEL[sub]}
                        </div>
                        <div className="text-[10.5px] tracking-widest uppercase text-navy-dark/45">
                          {enabled} / {list.length}
                        </div>
                      </div>
                      <ul className="space-y-1">
                        {list.map((s) => {
                          const on = !disabledSet.has(s.key);
                          const noteVal = form.service_notes?.[s.key] || "";
                          const noteOpen = openNoteKeys.has(s.key);
                          const hasNote = noteVal.trim().length > 0;
                          return (
                            <li key={s.key}>
                              <div className="flex items-start gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => toggleStdEnabled(s.key)}
                                  aria-pressed={on}
                                  data-testid={`arrival-inclusive-${s.key}${on ? "-on" : "-off"}`}
                                  className="flex-1 min-w-0 flex items-start gap-2 text-[13px] leading-snug text-left px-2 py-1.5 -mx-2 rounded hover:bg-ivory/60 transition-colors"
                                >
                                  <span
                                    className="mt-0.5 w-4 h-4 border rounded flex items-center justify-center shrink-0"
                                    style={on
                                      ? { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green }
                                      : { background: "#fff", borderColor: withAlpha(BRAND_COLORS.navy, 0.30) }}
                                  >
                                    {on && <Check className="w-2.5 h-2.5" style={{ color: BRAND_COLORS.ivory }} strokeWidth={3} />}
                                  </span>
                                  <span className={on ? "text-navy-dark" : "text-navy-dark/50 line-through decoration-navy-dark/25"}>
                                    {lang === "en" ? s.name_en : s.name_de}
                                  </span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => toggleNoteEditor(s.key)}
                                  aria-pressed={noteOpen}
                                  aria-label={de ? "Notiz hinzufügen" : "Add note"}
                                  data-testid={`arrival-inclusive-note-toggle-${s.key}`}
                                  className={`shrink-0 w-7 h-7 flex items-center justify-center border rounded transition-colors ${
                                    hasNote
                                      ? "border-gold bg-gold/15 text-gold hover:bg-gold/25"
                                      : "border-navy/20 text-navy-dark/50 hover:border-navy/50 hover:text-navy"
                                  }`}
                                  title={hasNote ? (de ? "Notiz bearbeiten" : "Edit note") : (de ? "Notiz hinzufügen" : "Add note")}
                                >
                                  <StickyNote className="w-3.5 h-3.5" strokeWidth={1.6} />
                                </button>
                              </div>
                              {noteOpen && (
                                <div className="mt-1 ml-6" data-testid={`arrival-inclusive-note-editor-${s.key}`}>
                                  <NoteTemplatesRow
                                    onPick={(t) => setNoteFor(s.key, t)}
                                    testPrefix={`arrival-inclusive-${s.key}`}
                                  />
                                  <textarea
                                    rows={2}
                                    value={noteVal}
                                    onChange={(e) => setNoteFor(s.key, e.target.value)}
                                    placeholder={de ? "Ihre Wünsche zu dieser Leistung …" : "Your wishes for this service …"}
                                    className="w-full bg-white border border-gold/40 focus:border-gold focus:outline-none px-2.5 py-1.5 text-[12.5px] text-navy-dark leading-snug"
                                  />
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {stdTotal === 0 && (
          <div className="text-[13px] text-navy-dark/60 italic" data-testid="arrival-inclusive-empty">
            {de ? "Für dieses Objekt sind keine Inklusivleistungen hinterlegt." : "No included services configured for this property."}
          </div>
        )}

        {/* Schritt-Navigation */}
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-navy/10">
          <button type="button" data-testid="arrival-step-inclusive-back" onClick={goBack}
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-navy border border-navy/30 px-6 py-3 hover:border-navy transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> {de ? "Zurück" : "Back"}
          </button>
          <button type="button" data-testid="arrival-step-inclusive-next" onClick={goNext}
            className="inline-flex items-center gap-3 text-ivory px-10 py-4 text-[13px] tracking-[0.2em] uppercase hover:opacity-90 transition-opacity"
            style={{ background: BRAND_COLORS.navy }}>
            {de ? "Weiter" : "Continue"} <ArrowRight className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
          </button>
        </div>
        </div>
        )}

        {/* ===== SCHRITT 3: ZUSATZLEISTUNGEN ===== */}
        {step === 2 && (
        <div className="space-y-8" data-testid="arrival-step-extras">

        {/* --- Extras / Services (Inline-Akkordeon · standardmäßig eingeklappt) --- */}
        {extras.length > 0 && (
          <div className="border border-navy/10 bg-white/60" data-testid="arrival-extras-section">
            <button
              type="button"
              onClick={() => setExtrasOpen((v) => !v)}
              data-testid="arrival-extras-toggle"
              className="w-full flex items-center justify-between text-left px-4 py-3.5"
              aria-expanded={extrasOpen}
            >
              <span className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
                <span className="text-[11px] tracking-[0.18em] uppercase text-navy-dark/85">
                  {de ? "Zusatzleistungen für diesen Zeitraum" : "Add-on services for this period"}
                </span>
                {form.services.length > 0 && (
                  <span
                    className="text-[10.5px] tracking-widest uppercase text-navy-dark/50"
                    data-testid="arrival-extras-count"
                  >
                    ({form.services.length} {de ? "gewählt" : "selected"})
                  </span>
                )}
              </span>
              <ChevronDown
                className={`w-4 h-4 text-navy-dark/50 transition-transform ${extrasOpen ? "rotate-180" : ""}`}
                strokeWidth={1.6}
              />
            </button>
            {!extrasOpen && (
              <div className="px-4 pb-3 -mt-1 text-[11.5px] text-navy-dark/55 italic leading-relaxed">
                {de ? "Optional · beliebig kombinierbar — antippen zum Öffnen." : "Optional · combine as you like — tap to open."}
              </div>
            )}
            {extrasOpen && (
            <div className="px-4 pb-4 pt-1">
            {lastExtras.length > 0 && (
              <div
                className="mb-6 p-5 border border-gold/40 bg-ivory/60"
                data-testid="arrival-last-extras"
              >
                <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
                    <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>
                      {de ? "Ihre letzte Buchung" : "Your last booking"}
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="apply-last-extras-btn"
                    onClick={applyLastExtras}
                    disabled={allLastApplied}
                    className="text-[11px] tracking-[0.15em] uppercase text-navy border border-navy px-4 py-2 hover:bg-navy hover:text-ivory transition-colors disabled:opacity-40 disabled:cursor-default"
                  >
                    {allLastApplied ? (de ? "✓ Übernommen" : "✓ Added") : (de ? `Alle ${lastExtras.length} übernehmen` : `Add all ${lastExtras.length}`)}
                  </button>
                </div>
                <div className="text-[12px] text-navy-dark/70 italic mb-3">
                  {de
                    ? "Beim letzten Mal haben Sie folgende Leistungen gewählt — mit einem Klick übernehmen oder einzeln antippen."
                    : "Last time you chose the following services — add them with one click, or tap individually."}
                </div>
                <div className="flex flex-wrap gap-2">
                  {lastExtras.map((ex) => {
                    const active = form.services.includes(ex.id);
                    return (
                      <button
                        key={ex.id}
                        type="button"
                        data-testid={`last-extra-${ex.key}`}
                        onClick={() => toggleExtra(ex.id)}
                        className={`text-left px-3 py-1.5 text-[12px] border transition-colors ${active ? "border-gold bg-gold text-navy" : "border-navy/25 bg-white text-navy hover:border-gold"}`}
                      >
                        {active ? "✓ " : "+ "}{lang === "en" ? ex.name_en : ex.name_de}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <div className="space-y-6">
              {Object.entries(extrasByCategory).map(([cat, list]) => (
                <div key={cat}>
                  <div className="font-display text-lg text-navy mb-3 flex items-baseline gap-3">
                    <span>{categoryLabels[cat] || cat}</span>
                    <span className="flex-1 border-b border-gold/40" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {list.map((ex) => {
                      const active = form.services.includes(ex.id);
                      const noteVal = form.service_notes?.[ex.id] || "";
                      const noteOpen = openNoteKeys.has(ex.id);
                      const hasNote = noteVal.trim().length > 0;
                      return (
                        <div
                          key={ex.id}
                          className={`border transition-colors ${active ? "border-gold bg-gold/5" : "border-navy/15 bg-white hover:border-navy/40"}`}
                        >
                          <div className="flex items-stretch">
                            <button
                              type="button"
                              data-testid={`arrival-extra-${ex.key}`}
                              onClick={() => toggleExtra(ex.id)}
                              className="flex-1 text-left p-3"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="text-[13px] text-navy leading-snug">{lang === "en" ? ex.name_en : ex.name_de}</div>
                                <div className={`shrink-0 w-4 h-4 border ${active ? "bg-gold border-gold" : "border-navy/30"}`} />
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleNoteEditor(ex.id)}
                              aria-pressed={noteOpen}
                              aria-label={de ? "Notiz hinzufügen" : "Add note"}
                              data-testid={`arrival-extra-note-toggle-${ex.key}`}
                              className={`shrink-0 w-9 flex items-center justify-center border-l transition-colors ${
                                hasNote
                                  ? "border-gold/40 bg-gold/10 text-gold"
                                  : "border-navy/10 text-navy-dark/50 hover:text-navy hover:bg-ivory/50"
                              }`}
                              title={hasNote ? (de ? "Notiz bearbeiten" : "Edit note") : (de ? "Notiz hinzufügen" : "Add note")}
                            >
                              <StickyNote className="w-3.5 h-3.5" strokeWidth={1.6} />
                            </button>
                          </div>
                          {noteOpen && (
                            <div className="border-t border-navy/10 px-3 py-2" data-testid={`arrival-extra-note-editor-${ex.key}`}>
                              <NoteTemplatesRow
                                onPick={(t) => setNoteFor(ex.id, t)}
                                testPrefix={`arrival-extra-${ex.key}`}
                              />
                              <textarea
                                rows={2}
                                value={noteVal}
                                onChange={(e) => setNoteFor(ex.id, e.target.value)}
                                placeholder={de ? "Ihre Wünsche zu dieser Leistung …" : "Your wishes for this service …"}
                                className="w-full bg-white border border-gold/40 focus:border-gold focus:outline-none px-2.5 py-1.5 text-[12.5px] text-navy-dark leading-snug"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            </div>
            )}
          </div>
        )}

        {extras.length === 0 && (
          <div className="text-[13px] text-navy-dark/60 italic" data-testid="arrival-extras-empty">
            {de ? "Zurzeit sind keine Zusatzleistungen verfügbar." : "No add-on services available right now."}
          </div>
        )}

        {/* Schritt-Navigation */}
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-navy/10">
          <button type="button" data-testid="arrival-step-extras-back" onClick={goBack}
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-navy border border-navy/30 px-6 py-3 hover:border-navy transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> {de ? "Zurück" : "Back"}
          </button>
          <button type="button" data-testid="arrival-step-extras-next" onClick={goNext}
            className="inline-flex items-center gap-3 text-ivory px-10 py-4 text-[13px] tracking-[0.2em] uppercase hover:opacity-90 transition-opacity"
            style={{ background: BRAND_COLORS.navy }}>
            {de ? "Weiter" : "Continue"} <ArrowRight className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
          </button>
        </div>
        </div>
        )}

        {/* ===== SCHRITT 4: BESONDERE WÜNSCHE ===== */}
        {step === 3 && (
        <div className="space-y-8" data-testid="arrival-step-wishes">

        {/* --- Notes --- */}
        <div>
          <label htmlFor="arrival-notes" className="label-caps block mb-2">
            {de ? "Besondere Wünsche" : "Special wishes"} <span className="lowercase text-navy-dark/50 italic">({de ? "optional" : "optional"})</span>
          </label>
          <textarea
            id="arrival-notes"
            data-testid="arrival-notes-input"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-4 py-3 text-navy-dark leading-relaxed"
            placeholder={de
              ? "Zum Beispiel Flughafen-Shuttle"
              : "For example airport shuttle"}
          />
        </div>

        {/* Schritt-Navigation: „Weiter" nur bei ausgefülltem Wunsch, sonst
            „Weiter ohne besondere Wünsche". KEIN Bestätigen in diesem Schritt. */}
        <div className="flex items-center justify-between gap-4 pt-4 border-t border-navy/10">
          <button type="button" data-testid="arrival-step-wishes-back" onClick={goBack}
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-navy border border-navy/30 px-6 py-3 hover:border-navy transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> {de ? "Zurück" : "Back"}
          </button>
          {form.notes.trim().length > 0 ? (
            <button type="button" data-testid="arrival-step-wishes-next" onClick={goNext}
              className="inline-flex items-center gap-3 text-ivory px-10 py-4 text-[13px] tracking-[0.2em] uppercase hover:opacity-90 transition-opacity"
              style={{ background: BRAND_COLORS.navy }}>
              {de ? "Weiter" : "Continue"} <ArrowRight className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
            </button>
          ) : (
            <button type="button" data-testid="arrival-skip-wishes-btn" onClick={goNext}
              className="inline-flex items-center gap-3 text-navy border border-navy/40 px-8 py-4 text-[12px] tracking-[0.16em] uppercase hover:bg-navy hover:text-ivory transition-colors">
              {de ? "Weiter ohne besondere Wünsche" : "Continue without special wishes"}
              <ArrowRight className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
            </button>
          )}
        </div>
        </div>
        )}

        {/* ===== SCHRITT 5: BESTÄTIGEN ===== */}
        {step === 4 && (
        <div className="space-y-8" data-testid="arrival-step-confirm">

        {/* --- Kurz-Zusammenfassung --- */}
        <div className="border border-gold/40 bg-ivory/50 rounded p-4" data-testid="arrival-summary">
          <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>
            {de ? "Ihre Zusammenfassung" : "Your summary"}
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-navy">
            <span className="flex items-center gap-2" data-testid="arrival-summary-period">
              <CalendarCheck className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              {form.from_date && form.to_date
                ? `${deDateMedium(form.from_date)} → ${deDateMedium(form.to_date)} · ${nights} ${de ? (nights === 1 ? "Nacht" : "Nächte") : (nights === 1 ? "night" : "nights")}`
                : (de ? "Zeitraum offen" : "Period not set")}
            </span>
            <span className="flex items-center gap-2" data-testid="arrival-summary-inclusive">
              <ShieldCheck className="w-4 h-4" style={{ color: STATUS_COLORS.green }} strokeWidth={1.6} />
              {stdEnabledCount} {de ? "Inklusivleistungen aktiv" : "included services active"}
            </span>
            <span className="flex items-center gap-2" data-testid="arrival-summary-extras">
              <Sparkles className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              {form.services.length} {de ? "Zusatzleistungen gewählt" : "add-on services selected"}
            </span>
            <span className="flex items-center gap-2" data-testid="arrival-summary-wishes">
              <StickyNote className="w-4 h-4" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
              {form.notes.trim().length > 0
                ? (de ? "Besondere Wünsche: ja" : "Special wishes: yes")
                : (de ? "Keine besonderen Wünsche" : "No special wishes")}
            </span>
          </div>
        </div>

        {/* --- Abschluss: Hinweis + Zurück · Bestätigen --- */}
        <div className="pt-4 border-t border-navy/10 space-y-4">
          <div className="text-[12px] text-navy-dark/60 italic">
            {de
              ? "Ihre Auswahl geht direkt an das Weissblom-Team und wird in den Kalender eingetragen. Die Abreise- und Anreise-Checkliste finden Sie danach direkt in der Abwesenheits-Kachel auf Ihrem Home-Screen."
              : "Your selection goes straight to the Weissblom team and is added to the calendar. You will find the departure and arrival checklists directly in the absence tile on your home screen."}
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <button type="button" data-testid="arrival-step-confirm-back" onClick={goBack}
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-navy border border-navy/30 px-6 py-3 hover:border-navy transition-colors">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.6} /> {de ? "Zurück" : "Back"}
            </button>
            <button
              type="submit"
              data-testid="arrival-confirm-btn"
              disabled={!canSubmit}
              className="inline-flex items-center gap-3 text-ivory px-10 py-4 text-[13px] tracking-[0.2em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
            >
              <CalendarCheck className="w-5 h-5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.4} />
              {submitting ? (de ? "Wird gesendet …" : "Sending …") : (de ? "Bestätigen" : "Confirm")}
            </button>
          </div>
        </div>
        </div>
        )}
      </form>
    </div>
  );
}
